import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Badge, useTheme } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

interface PhoneVerifiedBadgeProps {
  /** Whether phone is verified */
  isVerified: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Whether to show tooltip on web */
  showTooltip?: boolean;
  /** Test ID for testing */
  testID?: string;
}

export const PhoneVerifiedBadge: React.FC<PhoneVerifiedBadgeProps> = ({
  isVerified,
  size = 'medium',
  showTooltip = true,
  testID = 'phone-verified-badge',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const getIconSize = () => {
    switch (size) {
      case 'small':
        return 12;
      case 'large':
        return 24;
      default:
        return 16;
    }
  };

  const getBadgeSize = () => {
    switch (size) {
      case 'small':
        return 16;
      case 'large':
        return 32;
      default:
        return 24;
    }
  };

  if (!isVerified) {
    return null;
  }

  const iconSize = getIconSize();
  const badgeSize = getBadgeSize();

  return (
    <View
      style={[
        styles.container,
        {
          width: badgeSize,
          height: badgeSize,
          borderRadius: badgeSize / 2,
          backgroundColor: theme.colors.primary,
        },
      ]}
      testID={testID}
      accessibilityLabel={showTooltip ? t('phoneVerified', 'Phone verified') : undefined}
    >
      <MaterialCommunityIcons
        name="check"
        size={iconSize}
        color={theme.colors.onPrimary}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default PhoneVerifiedBadge;
